import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  History, 
  Download, 
  Filter,
  Loader2,
  User,
  Building2,
  AlertTriangle,
  FileText,
  LogIn,
  Trash2,
  Edit,
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Action type icons and colors
const ACTION_CONFIG = {
  CREATE: { icon: Plus, color: "text-green-400", bg: "bg-green-500/20", label: "Erstellt" },
  UPDATE: { icon: Edit, color: "text-blue-400", bg: "bg-blue-500/20", label: "Bearbeitet" },
  DELETE: { icon: Trash2, color: "text-red-400", bg: "bg-red-500/20", label: "Gelöscht" },
  LOGIN: { icon: LogIn, color: "text-yellow-400", bg: "bg-yellow-500/20", label: "Anmeldung" },
  PASSWORD_RESET: { icon: User, color: "text-purple-400", bg: "bg-purple-500/20", label: "Passwort" }
};

// Entity type icons
const ENTITY_CONFIG = {
  incident: { icon: AlertTriangle, label: "Meldung" },
  user: { icon: User, label: "Benutzer" },
  company: { icon: Building2, label: "Unternehmen" }
};

export default function AuditLog({ token }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(25);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    entity_type: "",
    action: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Detail dialog
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (filters.entity_type) params.append("entity_type", filters.entity_type);
      if (filters.action) params.append("action", filters.action);
      
      const response = await axios.get(`${API}/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      toast.error("Fehler beim Laden der Audit-Logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity_type) params.append("entity_type", filters.entity_type);
      if (filters.action) params.append("action", filters.action);
      
      const response = await axios.get(`${API}/audit-logs/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_log_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Audit-Log exportiert");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Fehler beim Exportieren");
    } finally {
      setExporting(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="audit-log">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-500 mb-2">COMPLIANCE</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-50" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Audit-Log
          </h1>
          <p className="text-zinc-500 mt-2">Alle Änderungen nachvollziehbar dokumentiert</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="border-zinc-700 text-zinc-300"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-yellow-500 text-black hover:bg-yellow-400"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exportieren
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
            <p className="text-2xl font-bold text-zinc-50">{stats.total}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Gesamt</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
            <p className="text-2xl font-bold text-zinc-50">{stats.recent_7_days}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Letzte 7 Tage</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
            <p className="text-2xl font-bold text-green-400">{stats.by_action?.CREATE || 0}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Erstellt</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
            <p className="text-2xl font-bold text-blue-400">{stats.by_action?.UPDATE || 0}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Bearbeitet</p>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Typ</Label>
              <Select
                value={filters.entity_type || "all"}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, entity_type: value === "all" ? "" : value }));
                  setPage(1);
                }}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                  <SelectValue placeholder="Alle Typen" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="all" className="text-zinc-50">Alle Typen</SelectItem>
                  <SelectItem value="incident" className="text-zinc-50">Meldungen</SelectItem>
                  <SelectItem value="user" className="text-zinc-50">Benutzer</SelectItem>
                  <SelectItem value="company" className="text-zinc-50">Unternehmen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Aktion</Label>
              <Select
                value={filters.action || "all"}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, action: value === "all" ? "" : value }));
                  setPage(1);
                }}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                  <SelectValue placeholder="Alle Aktionen" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="all" className="text-zinc-50">Alle Aktionen</SelectItem>
                  <SelectItem value="CREATE" className="text-zinc-50">Erstellt</SelectItem>
                  <SelectItem value="UPDATE" className="text-zinc-50">Bearbeitet</SelectItem>
                  <SelectItem value="DELETE" className="text-zinc-50">Gelöscht</SelectItem>
                  <SelectItem value="LOGIN" className="text-zinc-50">Anmeldung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setFilters({ entity_type: "", action: "" });
                  setPage(1);
                }}
                className="border-zinc-700 text-zinc-300"
              >
                Filter zurücksetzen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Log List */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-20">
            <History className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
            <p className="text-zinc-500">Keine Einträge gefunden</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800 bg-zinc-950">
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Zeit</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Aktion</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Typ</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Objekt</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Benutzer</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Unternehmen</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const actionConfig = ACTION_CONFIG[log.action] || { icon: FileText, color: "text-zinc-400", bg: "bg-zinc-500/20", label: log.action };
                    const entityConfig = ENTITY_CONFIG[log.entity_type] || { icon: FileText, label: log.entity_type };
                    const ActionIcon = actionConfig.icon;
                    const EntityIcon = entityConfig.icon;
                    
                    return (
                      <tr 
                        key={log.id} 
                        className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors"
                        onClick={() => setSelectedLog(log)}
                        data-testid={`audit-log-${log.id}`}
                      >
                        <td className="p-4 text-sm text-zinc-400">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-zinc-600" />
                            {formatTimestamp(log.timestamp)}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs font-medium ${actionConfig.bg} ${actionConfig.color}`}>
                            <ActionIcon className="w-3 h-3" />
                            {actionConfig.label}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="inline-flex items-center gap-1.5 text-sm text-zinc-400">
                            <EntityIcon className="w-4 h-4 text-zinc-500" />
                            {entityConfig.label}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-zinc-300 max-w-xs truncate">
                          {log.entity_name || log.entity_id || "-"}
                        </td>
                        <td className="p-4 text-sm text-zinc-400">
                          {log.user_name || log.user_email || "-"}
                        </td>
                        <td className="p-4 text-sm text-zinc-500">
                          {log.company_name || "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-zinc-800 bg-zinc-950">
              <p className="text-sm text-zinc-500">
                Zeige {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} von {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="border-zinc-700 text-zinc-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-zinc-400 px-2">
                  Seite {page} von {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="border-zinc-700 text-zinc-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              Audit-Log Details
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Vollständige Informationen zu diesem Eintrag
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-950 p-3 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Zeitstempel</p>
                  <p className="text-zinc-200">{formatTimestamp(selectedLog.timestamp)}</p>
                </div>
                <div className="bg-zinc-950 p-3 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Aktion</p>
                  <p className={ACTION_CONFIG[selectedLog.action]?.color || "text-zinc-200"}>
                    {ACTION_CONFIG[selectedLog.action]?.label || selectedLog.action}
                  </p>
                </div>
                <div className="bg-zinc-950 p-3 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Typ</p>
                  <p className="text-zinc-200">{ENTITY_CONFIG[selectedLog.entity_type]?.label || selectedLog.entity_type}</p>
                </div>
                <div className="bg-zinc-950 p-3 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Objekt</p>
                  <p className="text-zinc-200">{selectedLog.entity_name || selectedLog.entity_id || "-"}</p>
                </div>
                <div className="bg-zinc-950 p-3 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Benutzer</p>
                  <p className="text-zinc-200">{selectedLog.user_name || "-"}</p>
                  <p className="text-zinc-500 text-sm">{selectedLog.user_email || ""}</p>
                </div>
                <div className="bg-zinc-950 p-3 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Unternehmen</p>
                  <p className="text-zinc-200">{selectedLog.company_name || "-"}</p>
                </div>
              </div>
              
              {selectedLog.details && (
                <div className="bg-zinc-950 p-3 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Details</p>
                  <p className="text-zinc-200">{selectedLog.details}</p>
                </div>
              )}
              
              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                <div className="bg-zinc-950 p-3 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Änderungen</p>
                  <div className="space-y-2">
                    {Object.entries(selectedLog.changes).map(([field, vals]) => (
                      <div key={field} className="flex items-start gap-2 text-sm">
                        <span className="text-yellow-500 font-medium min-w-[120px]">{field}:</span>
                        <span className="text-red-400 line-through">{String(vals.old) || "(leer)"}</span>
                        <span className="text-zinc-500">→</span>
                        <span className="text-green-400">{String(vals.new) || "(leer)"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
